/**
 * Pure helpers behind the Saf3AI Agent page: the message model, how it is
 * stored and restored through the AI microservice, exports, slash commands
 * and the time window. No React, no DOM — everything here is unit-tested.
 */

/** Conversations the full page saves; the header chat panel uses 'chat_panel'. */
export const AGENT_SURFACE = 'ai_explorer';
export const HISTORY_SURFACES = ['ai_explorer', 'chat_panel'];
export const isAgentHistoryItem = (i) => !!i && (!i.surface || HISTORY_SURFACES.includes(i.surface)) && i.agent_mode !== 'helper';

/** The backend keeps at most 500 messages per conversation; keep the tail. */
export const MAX_STORED_MESSAGES = 200;

/**
 * Time windows the insights endpoint accepts. The same token travels in
 * `page_context.time_range` so an answer uses the window the page shows.
 */
export const TIME_WINDOWS = [
  { id: '24h', param: 'last_24_hours' },
  { id: '7d', param: 'last_7_days' },
  { id: '30d', param: 'last_30_days' },
  { id: '90d', param: 'last_90_days' },
  { id: '365d', param: 'last_365_days' },
];
export const DEFAULT_WINDOW = '30d';
export const WINDOW_STORAGE_KEY = 'saf3ai_agent_time_window';

export const windowParam = (id) =>
  (TIME_WINDOWS.find((w) => w.id === id) || TIME_WINDOWS.find((w) => w.id === DEFAULT_WINDOW)).param;

let seq = 0;
export const newId = () => `m${Date.now().toString(36)}${(seq += 1)}`;

export const userMessage = (content) => ({
  id: newId(), role: 'user', content, ts: new Date().toISOString(),
});

/**
 * A row the page shows but never saves: the "You're on X" greeting with its
 * context cards, or a context-switch notice after navigating with the panel open.
 */
export const noticeMessage = (content, contextCards = []) => ({
  id: newId(), role: 'agent', ephemeral: true, content, contextCards: contextCards.length ? contextCards : undefined,
});

export const pendingAgentMessage = () => ({
  id: newId(), role: 'agent', content: '', loading: true, streaming: false, ts: new Date().toISOString(),
});

/**
 * What goes to the backend. Transient UI state (loading, streaming, follow-up
 * chips) is dropped; the structured payload behind a chart or table is kept so
 * the conversation renders the same when reopened.
 */
export const toStoredMessages = (messages = []) => messages
  .filter((m) => m && m.content && !m.loading && !m.ephemeral)
  .slice(-MAX_STORED_MESSAGES)
  .map((m) => ({
    // Kept so a canvas tile can point back at the answer it was pinned from.
    ...(typeof m.id === 'string' && m.id ? { id: m.id } : {}),
    role: m.role,
    content: m.content,
    ...(m.displayType && m.displayType !== 'text' ? { display_type: m.displayType } : {}),
    ...(m.chartType ? { chart_type: m.chartType } : {}),
    ...(m.data ? { data: m.data } : {}),
    ...(m.error ? { error: true } : {}),
    ...(Array.isArray(m.steps) && m.steps.length ? { steps: m.steps } : {}),
    ...(Array.isArray(m.approvals) && m.approvals.length ? { approvals: m.approvals } : {}),
    ...(m.ts ? { ts: m.ts } : {}),
  }));

/** Stored messages back into the live model. Unknown shapes become plain text. */
export const fromStoredMessages = (stored = []) => (Array.isArray(stored) ? stored : [])
  .filter((m) => m && typeof m.content === 'string' && m.content)
  .map((m) => ({
    id: typeof m.id === 'string' && /^[\w-]{1,64}$/.test(m.id) ? m.id : newId(),
    role: m.role === 'user' ? 'user' : 'agent',
    content: m.content,
    displayType: m.display_type || 'text',
    chartType: m.chart_type || null,
    data: m.data || null,
    error: !!m.error,
    steps: Array.isArray(m.steps) ? m.steps : undefined,
    approvals: Array.isArray(m.approvals) ? m.approvals : undefined,
    ts: m.ts || null,
  }));

export const hasUserTurn = (messages = []) => messages.some((m) => m && m.role === 'user' && m.content);

export const conversationTitle = (messages = [], fallback = 'Untitled conversation') => {
  const first = messages.find((m) => m && m.role === 'user' && m.content);
  return first ? String(first.content).replace(/\s+/g, ' ').trim().slice(0, 80) : fallback;
};

/** The last `limit` turns, stripped to what the model needs for context. */
export const historyForModel = (messages = [], limit = 6) => messages
  .filter((m) => m && m.content && !m.loading && !m.error)
  .slice(-limit)
  .map((m) => ({ role: m.role, content: String(m.content).slice(0, 500) }));

/** Where to show a table: a `complete` payload, else the first markdown table in the text. */
export const tableFromMessage = (m) => {
  if (!m) return null;
  if (m.data && Array.isArray(m.data.headers) && Array.isArray(m.data.rows)) {
    return { headers: m.data.headers, rows: m.data.rows };
  }
  if (m.data && Array.isArray(m.data.tables) && m.data.tables[0]?.headers) {
    return { headers: m.data.tables[0].headers, rows: m.data.tables[0].rows || [] };
  }
  const lines = String(m.content || '').split('\n').map((l) => l.trim());
  const start = lines.findIndex((l, i) => /^\|.*\|$/.test(l) && /^\|[\s:|-]+\|$/.test(lines[i + 1] || ''));
  if (start < 0) return null;
  const split = (l) => l.slice(1, -1).split('|').map((c) => c.trim());
  const headers = split(lines[start]);
  const rows = [];
  for (let i = start + 2; i < lines.length && /^\|.*\|$/.test(lines[i]); i += 1) rows.push(split(lines[i]));
  return { headers, rows };
};

const csvCell = (v) => {
  const s = v == null ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export const tableToCsv = ({ headers = [], rows = [] }) =>
  [headers, ...rows].map((r) => (Array.isArray(r) ? r : []).map(csvCell).join(',')).join('\n');

/** The whole conversation as Markdown, the form that pastes well into a ticket or doc. */
export const transcriptMarkdown = (messages = [], { title, agentLabel = 'Saf3AI Agent', userLabel = 'You' } = {}) => {
  const out = [`# ${title || conversationTitle(messages)}`, ''];
  messages.filter((m) => m && m.content && !m.loading).forEach((m) => {
    out.push(`**${m.role === 'user' ? userLabel : agentLabel}**${m.ts ? ` · ${m.ts}` : ''}`, '', m.content, '');
    const table = m.role === 'agent' && m.data ? tableFromMessage(m) : null;
    if (table && !/^\|/m.test(m.content)) {
      out.push(`| ${table.headers.join(' | ')} |`, `| ${table.headers.map(() => '---').join(' | ')} |`);
      table.rows.forEach((r) => out.push(`| ${(r || []).join(' | ')} |`));
      out.push('');
    }
  });
  return out.join('\n');
};

/**
 * Slash commands. Each does something the page can really do; there is no
 * command that only pretends. `run` names the page action to invoke.
 */
export const SLASH_COMMANDS = [
  { id: 'new', usage: '/new', run: 'newChat' },
  { id: 'export', usage: '/export', run: 'exportTranscript' },
  { id: 'pin', usage: '/pin', run: 'togglePin' },
  { id: 'window', usage: '/window 7d', run: 'setWindow', arg: true },
  { id: 'shortcuts', usage: '/shortcuts', run: 'showShortcuts' },
];

/** The commands matching what has been typed so far, or none when the text is not a command. */
export const matchSlashCommands = (text) => {
  const m = /^\/(\S*)$/.exec(String(text || '').trimStart().split(/\s/)[0] || '');
  if (!m) return [];
  const prefix = m[1].toLowerCase();
  return SLASH_COMMANDS.filter((c) => c.id.startsWith(prefix));
};

/** Parse a fully typed command: `{ command, arg }`, or null for ordinary text. */
export const parseSlashCommand = (text) => {
  const m = /^\/(\S+)(?:\s+(.*))?$/.exec(String(text || '').trim());
  if (!m) return null;
  const command = SLASH_COMMANDS.find((c) => c.id === m[1].toLowerCase());
  return command ? { command, arg: (m[2] || '').trim() } : null;
};

/** Group history items by day for the pane: Pinned, Today, Yesterday, Previous 7 days, Older. */
export const groupHistory = (items = [], now = new Date()) => {
  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const today = startOfDay(now);
  const day = 86400000;
  const groups = { pinned: [], today: [], yesterday: [], week: [], older: [] };
  items.forEach((it) => {
    if (!it) return;
    if (it.bookmarked) { groups.pinned.push(it); return; }
    const t = it.updated_at || it.created_at;
    const ts = t ? startOfDay(new Date(t)) : 0;
    if (ts >= today) groups.today.push(it);
    else if (ts >= today - day) groups.yesterday.push(it);
    else if (ts >= today - 7 * day) groups.week.push(it);
    else groups.older.push(it);
  });
  return groups;
};

/** A friendlier error line than the raw fetch message. Returns an i18n key. */
export const errorKey = (error) => {
  const msg = String(error?.message || error || '');
  if (error?.name === 'AbortError') return 'agent.stopped';
  if (/Network error|Failed to fetch/i.test(msg)) return 'agent.errorNetwork';
  if (/HTTP error|status: 5/i.test(msg)) return 'agent.errorServer';
  if (/Unauthorized/i.test(msg)) return 'agent.errorUnauthorized';
  return 'agent.errorGeneric';
};

/** Per-user tool preferences: the master switch and the tools switched off. */
export const TOOLS_STORAGE_KEY = 'saf3ai_agent_tools';
export const readToolPrefs = () => {
  try {
    const v = JSON.parse(localStorage.getItem(TOOLS_STORAGE_KEY) || '{}');
    return { useTools: v.useTools !== false, exclude: Array.isArray(v.exclude) ? v.exclude : [] };
  } catch { return { useTools: true, exclude: [] }; }
};
export const writeToolPrefs = (prefs) => {
  try { localStorage.setItem(TOOLS_STORAGE_KEY, JSON.stringify(prefs)); } catch { /* private mode */ }
};

/**
 * Fold one tool-plane event into the agent message it belongs to. Steps are
 * what the agent called (running → done/error/held); an approval is a write
 * the agent wants to make and the user has not yet allowed; a notice is a
 * reason the answer came without tools.
 */
export const applyToolEvent = (m, ev) => {
  const steps = Array.isArray(m.steps) ? m.steps : [];
  switch (ev?.type) {
    case 'tool_call':
      return { ...m, loading: false, steps: [...steps, { id: ev.id, name: ev.name, arguments: ev.arguments || {}, write: !!ev.write, status: 'running' }] };
    case 'tool_result':
      return { ...m, steps: steps.map((s) => (s.id === ev.id ? { ...s, status: ev.ok ? 'done' : 'error', summary: ev.summary || '', ms: ev.ms } : s)) };
    case 'approval_required':
      return {
        ...m,
        steps: steps.map((s) => (s.id === ev.id ? { ...s, status: 'held', summary: '' } : s)),
        approvals: [...(m.approvals || []), { id: ev.id, name: ev.name, arguments: ev.arguments || {}, status: 'pending' }],
      };
    case 'notice':
      return { ...m, notices: [...(m.notices || []), { code: ev.code || 'notice', message: ev.message || '' }] };
    case 'error':
      return { ...m, notices: [...(m.notices || []), { code: 'error', message: ev.message || '' }] };
    default:
      return m;
  }
};

export const setApproval = (m, id, patch) => ({
  ...m, approvals: (m.approvals || []).map((a) => (a.id === id ? { ...a, ...patch } : a)),
});

/** "Worked for 14s · 4 steps" needs a total; running steps count as 0. */
export const stepsDuration = (steps = []) => Math.round(steps.reduce((t, s) => t + (s.ms || 0), 0) / 100) / 10;
